// /app/frontend/src/contexts/RBACContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';

// Module definitions for UI mapping (expanded)
export const MODULE_CONFIG = {
  // Compliance modules
  ropa: { label: 'ROPA', icon: 'Database', path: '/compliance/ropa', category: 'compliance' },
  data_flow: { label: 'Data Flow Map', icon: 'Network', path: '/compliance/data-flows', category: 'compliance' },
  breach: { label: 'Data Breach', icon: 'FileWarning', path: '/compliance/breaches', category: 'compliance' },
  dsr: { label: 'DSR Management', icon: 'UserCheck', path: '/compliance/dsr', category: 'compliance' },
  vendor: { label: 'Vendor Management', icon: 'Briefcase', path: '/compliance/vendors', category: 'compliance' },
  maturity: { label: 'Maturity Assessment', icon: 'Target', path: '/compliance/maturity', category: 'compliance' },
  // Assessment modules
  due_diligence: { label: 'Due Diligence', icon: 'ClipboardCheck', path: '/compliance/due-diligence', category: 'assessments' },
  staff_assessment: { label: 'Staff Assessments', icon: 'ClipboardList', path: '/compliance/questionnaires', category: 'assessments' },
  readiness: { label: 'Readiness Assessment', icon: 'CheckSquare', path: '/compliance/readiness', category: 'assessments' },
  // Audit & Assurance modules
  risk_assessment: { label: 'Risk Assessments', icon: 'AlertTriangle', path: '/risk/assessments', category: 'audit' },
  risk_register: { label: 'Risk Register', icon: 'FileText', path: '/risk/register', category: 'audit' },
  // Operations modules
  tasks: { label: 'Tasks', icon: 'ClipboardList', path: '/operations/tasks', category: 'operations' },
  documents: { label: 'Documents', icon: 'FolderOpen', path: '/operations/documents', category: 'operations' },
};

// All available modules
export const ALL_MODULES = Object.keys(MODULE_CONFIG);

// Tier default modules
export const TIER_DEFAULTS = {
  starter: ['ropa', 'data_flow', 'breach', 'dsr', 'tasks', 'documents'],
  sme: ['ropa', 'data_flow', 'breach', 'dsr', 'vendor', 'readiness', 'risk_assessment', 'risk_register', 'tasks', 'documents'],
  enterprise: ALL_MODULES,
};

// Module categories for UI grouping
export const MODULE_CATEGORIES = {
  compliance: { label: 'Compliance', modules: ['maturity', 'ropa', 'data_flow', 'breach', 'dsr', 'vendor'] },
  assessments: { label: 'Assessments', modules: ['due_diligence', 'staff_assessment', 'readiness'] },
  audit: { label: 'Audit & Assurance', modules: ['risk_assessment', 'risk_register'] },
  operations: { label: 'Operations', modules: ['tasks', 'documents'] },
};

const RBACContext = createContext(null);

export function RBACProvider({ children }) {
  const [enabledModules, setEnabledModules] = useState(ALL_MODULES);
  const [subscriptionTier, setSubscriptionTier] = useState('enterprise');
  const [isDPO, setIsDPO] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [subdomain, setSubdomain] = useState(null);

  const fetchModules = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setIsLoading(false);
        return;
      }

      const response = await authAPI.getMyModules();
      const data = response.data;
      
      setEnabledModules(data.enabled_modules || ALL_MODULES);
      setSubscriptionTier(data.subscription_tier || 'enterprise');
      setIsDPO(data.is_dpo || false);
      setSubdomain(data.subdomain || null);
    } catch (error) {
      console.error('Failed to fetch modules:', error);
      // Default to all modules on error (graceful degradation)
      setEnabledModules(ALL_MODULES);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchModules();
  }, [fetchModules]);

  // Check if a specific module is enabled
  const hasModule = useCallback((moduleId) => {
    return enabledModules.includes(moduleId);
  }, [enabledModules]);

  // Check if user has any of the specified modules
  const hasAnyModule = useCallback((moduleIds) => {
    return moduleIds.some(id => enabledModules.includes(id));
  }, [enabledModules]);

  // Check if user can access a path based on modules
  const canAccessPath = useCallback((path) => {
    // DPO can access everything
    if (isDPO) return true;

    // Check if path requires a specific module
    for (const [moduleId, config] of Object.entries(MODULE_CONFIG)) {
      if (path.startsWith(config.path)) {
        return enabledModules.includes(moduleId);
      }
    }
    
    // Allow access to paths not associated with modules (dashboard, settings, etc.)
    return true;
  }, [enabledModules, isDPO]);

  // Refresh modules (call after login or subscription change)
  const refreshModules = useCallback(() => {
    setIsLoading(true);
    fetchModules();
  }, [fetchModules]);

  const value = {
    enabledModules,
    subscriptionTier,
    isDPO,
    isLoading,
    subdomain,
    hasModule,
    hasAnyModule,
    canAccessPath,
    refreshModules,
    MODULE_CONFIG,
    ALL_MODULES,
  };

  return (
    <RBACContext.Provider value={value}>
      {children}
    </RBACContext.Provider>
  );
}

export function useRBAC() {
  const context = useContext(RBACContext);
  if (!context) {
    throw new Error('useRBAC must be used within an RBACProvider');
  }
  return context;
}

export default RBACContext;
